import {
  Box,
  Card,
  CardContent,
  TextField,
  Theme,
  Typography,
} from '@mui/material'
import React, { useMemo } from 'react'
import CardFormHeader from '../../Card/CardFormHeader'
import Header, { HeaderVariant } from '../../Elements/Header'
import { TFunction, useTranslation } from 'react-i18next'
import { useShowOnDesktop } from '../../../hooks/useShowOnDesktop'
import { useNotificationContext } from '../../Context/NotificationContext'
import { Notification } from '../../../swagger'
import { NotificationBannerAlert } from './NotificationBannerAlert'
import DropDown, { DropDownVariant, MenuOption } from '../../Menus/DropDown'
import { useTheme } from '@mui/material'
import DateField, { DateFieldVariant } from '../../Elements/DateField'
import { createSafeDate, dateToDashString } from '../../../utils/dateUtility'
import { parse } from 'date-fns'

export const getValueFromColor = (
  t: TFunction,
  theme: Theme,
  color: string
): string => {
  switch (color) {
    case theme.palette.alert.error:
      return t(
        'Admin.Notifications.NotificationInformationCard.ErrorColor',
        'Critical System Issue'
      )
    case theme.palette.alert.warning:
    default:
      return t(
        'Admin.Notifications.NotificationInformationCard.WarningColor',
        'Maintenance'
      )
    case theme.palette.alert.success:
      return t(
        'Admin.Notifications.NotificationInformationCard.WarningColor',
        'Fixed Issue'
      )
  }
}

export const NotificationInformationCard: React.FC<{
  isEditMode?: boolean
}> = ({ isEditMode }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const showOnDesktop = useShowOnDesktop()
  const {
    notificationDetails,
    updateNotificationDetails,
    formValidity,
    updateFormValidity,
  } = useNotificationContext()
  const [, startTime] = notificationDetails?.startDate
    .toISOString()
    .split('T') ?? ['', '']
  const startDate = notificationDetails?.startDate
    ? dateToDashString(notificationDetails?.startDate)
    : ''

  const [, endTime] = notificationDetails?.endDate.toISOString().split('T') ?? [
    '',
    '',
  ]
  const endDate = notificationDetails?.endDate
    ? dateToDashString(notificationDetails?.endDate)
    : ''

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target
    let updatedValue: string | Date = value
    let updatedName = name
    switch (name) {
      case 'startDate':
        updatedValue = parse(
          value + 'T' + startTime,
          "MM-dd-yyyy'T'HH:mm:ss.SSSX",
          new Date()
        )

        updatedName = 'startDate'
        updateFormValidity({
          ...formValidity,
          startDate: {
            input: !!startDate && !isNaN(createSafeDate(startDate).getTime()),
            afterPrevious: notificationDetails.endDate > updatedValue,
          },
          startTime: {
            input: !!startTime,
            afterPrevious: true,
          },
          endDate: {
            ...formValidity.endDate,
            afterPrevious: true,
          },
          endTime: {
            ...formValidity.endTime,
            afterPrevious: true,
          },
        })
        break
      case 'startTime':
        updatedValue = parse(
          startDate + 'T' + value + '.000Z',
          "MM-dd-yyyy'T'HH:mm:ss.SSSX",
          new Date()
        )
        updatedName = 'startDate'
        updateFormValidity({
          ...formValidity,
          startTime: {
            input: !!startTime,
            afterPrevious: notificationDetails.endDate > updatedValue,
          },
          startDate: {
            ...formValidity.startDate,
            afterPrevious: true,
          },
          endDate: {
            ...formValidity.endDate,
            afterPrevious: true,
          },
          endTime: {
            ...formValidity.endTime,
            afterPrevious: true,
          },
        })
        break
      case 'endDate':
        updatedValue = parse(
          value + 'T' + endTime,
          "MM-dd-yyyy'T'HH:mm:ss.SSSX",
          new Date()
        )

        updatedName = 'endDate'
        updateFormValidity({
          ...formValidity,
          endDate: {
            input: !!endDate && !isNaN(createSafeDate(endDate).getTime()),
            afterPrevious: updatedValue > notificationDetails.startDate,
          },
          startTime: {
            input: !!startTime,
            afterPrevious: true,
          },
          startDate: {
            input: !!startDate,
            afterPrevious: true,
          },
          endTime: {
            ...formValidity.endTime,
            afterPrevious: true,
          },
        })
        break
      case 'endTime':
        updatedValue = parse(
          endDate + 'T' + value + '.000Z',
          "MM-dd-yyyy'T'HH:mm:ss.SSSX",
          new Date()
        )
        updatedName = 'endDate'
        updateFormValidity({
          ...formValidity,
          endTime: {
            input: !!endTime,
            afterPrevious: updatedValue > notificationDetails.startDate,
          },
          startTime: {
            input: !!startTime,
            afterPrevious: true,
          },
          startDate: {
            input: !!startDate,
            afterPrevious: true,
          },
          endDate: {
            ...formValidity.endDate,
            afterPrevious: true,
          },
        })
        break
      default:
        updateFormValidity({
          ...formValidity,
          [name]: {
            input: !!updatedValue,
          },
        })
        break
    }
    updateNotificationDetails({
      ...notificationDetails,
      [updatedName]: updatedValue,
    } as Notification)
  }

  const commonStyles = {
    paddingBottom: 2,
    display: 'flex',
    flexDirection: showOnDesktop ? 'row' : 'column',
  }

  const mobileStyles = {
    width: showOnDesktop ? '50%' : '100%',
  }

  const BannerPreview = useMemo(() => {
    return (
      <NotificationBannerAlert
        userNotification={{
          ...notificationDetails,
          name: notificationDetails.name,
        }}
        stayOpen
      />
    )
  }, [notificationDetails])

  const onTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9\-]/.test(e.key) &&
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(
        e.key
      )
    ) {
      e.preventDefault()
    }
  }

  const colorOptions: MenuOption[] = [
    {
      id: 'error',
      name: t(
        'Admin.Notifications.NotificationInformationCard.CriticalSystemIssue',
        'Critical System Issue'
      ),
    },
    {
      id: 'warning',
      name: t(
        'Admin.Notifications.NotificationInformationCard.Maintenance',
        'Maintenance'
      ),
    },
    {
      id: 'success',
      name: t(
        'Admin.Notifications.NotificationInformationCard.FixedIssue',
        'Fixed Issue'
      ),
    },
  ]

  return (
    <Card>
      <CardFormHeader
        header={
          <Header
            id="notification-information-header"
            headerName={t(
              'Admin.Notifications.NotificationInformationCard.Header',
              'Notification Information'
            )}
            component="h2"
            variant={HeaderVariant.Card}
          />
        }
      />
      <Box mx={2}>
        <Typography variant="subtitle1" fontWeight={'bold'}>
          Banner Preview
        </Typography>
        {BannerPreview}
      </Box>
      <CardContent>
        <form>
          <Box sx={{ ...commonStyles, height: '100%' }}>
            <Box width={mobileStyles.width} pb={showOnDesktop ? 0 : 2}>
              <TextField
                value={notificationDetails?.name ?? ''}
                name="name"
                onChange={handleChange}
                label={t(
                  'Admin.Notifications.NotificationInformationCard.NotificationName',
                  'Notification Name*'
                )}
                variant="filled"
                fullWidth
                disabled={!isEditMode}
                error={!formValidity.name.input}
                helperText={
                  !formValidity.name.input
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.NotificationNameError',
                        'Name cannot be empty.'
                      )
                    : ''
                }
              />
            </Box>
            <Box
              width={mobileStyles.width}
              pl={showOnDesktop ? 2 : 0}
              height={'64px'}
            >
              <DropDown
                id={'color'}
                menuOptions={colorOptions}
                value={getValueFromColor(t, theme, notificationDetails.color)}
                defaultValue={colorOptions[1].name}
                disabled={!isEditMode}
                handleSelection={function (
                  _selection: string,
                  _: string,
                  selectOptionId: string | number
                ): void {
                  updateNotificationDetails({
                    ...notificationDetails,
                    color: theme.palette.alert[
                      selectOptionId as keyof typeof theme.palette.alert
                    ] as string,
                  })
                }}
                fullWidth
                label={t(
                  'Admin.Notifications.NotificationInformationCard.NotificationColor',
                  'Notification Type'
                )}
                cssProps={{
                  padding: 0,
                  height: 'auto',
                  width: '100%',
                  textAlign: 'left',
                }}
                variant={DropDownVariant.FormField}
                helperText={t(
                  'Admin.Notifications.NotificationInformationCard.NotificationNameError',
                  'Adjust the background color of the banner.'
                )}
              />
            </Box>
          </Box>
          <Box sx={{ ...commonStyles }}>
            <Box width={mobileStyles.width} pb={showOnDesktop ? 0 : 2}>
              <DateField
                label={t(
                  'Admin.Notifications.NotificationInformationCard.StartDate',
                  'Start Date*'
                )}
                type="date"
                name="startDate"
                value={startDate}
                onChangeForParent={handleChange}
                variant="filled"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!isEditMode}
                error={
                  !formValidity.startDate.input ||
                  !formValidity.startDate.afterPrevious
                }
                helperTextForParent={
                  !formValidity.startDate.input
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.StartDateError',
                        'Start Date must be a valid date.'
                      )
                    : !formValidity.startDate.afterPrevious
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.StartDateAfterEndDateError',
                        'Start Date must be before End Date.'
                      )
                    : ''
                }
                dateVariant={DateFieldVariant.NotificationStartDate}
              />
            </Box>
            <Box width={mobileStyles.width} pl={showOnDesktop ? 2 : 0}>
              <TextField
                type="time"
                label={t(
                  'Admin.Notifications.NotificationInformationCard.StartTime',
                  'Start Time (GMT)*'
                )}
                name="startTime"
                value={startTime.split('.')[0]}
                onChange={handleChange}
                variant="filled"
                inputProps={{ step: 1 }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!isEditMode}
                error={
                  !formValidity.startTime.input ||
                  !formValidity.startTime.afterPrevious
                }
                helperText={
                  !formValidity.startTime.input
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.StartTimeError',
                        'Start Time must be a valid time.'
                      )
                    : !formValidity.startTime.afterPrevious
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.StartDateAfterEndDateError',
                        'Start Time must be before End Time.'
                      )
                    : ''
                }
              />
            </Box>
          </Box>

          <Box sx={{ ...commonStyles }}>
            <Box width={mobileStyles.width} pb={showOnDesktop ? 0 : 2}>
              <DateField
                label={t(
                  'Admin.Notifications.NotificationInformationCard.EndDate',
                  'End Date*'
                )}
                type="date"
                name="endDate"
                value={endDate}
                onChangeForParent={handleChange}
                variant="filled"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!isEditMode}
                error={
                  !formValidity.endDate.input ||
                  !formValidity.endDate.afterPrevious
                }
                helperTextForParent={
                  !formValidity.endDate.input
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.EndDateError',
                        'End Date must be a valid date.'
                      )
                    : !formValidity.endDate.afterPrevious
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.EndDateBeforeStartDateError',
                        'End Date must be after the Start Date.'
                      )
                    : ''
                }
                dateVariant={DateFieldVariant.NotificationEndDate}
              />
            </Box>
            <Box width={mobileStyles.width} pl={showOnDesktop ? 2 : 0}>
              <TextField
                label={t(
                  'Admin.Notifications.NotificationInformationCard.EndTime',
                  'End Time (GMT)*'
                )}
                name="endTime"
                type="time"
                value={endTime.split('.')[0]}
                onChange={handleChange}
                onKeyDown={onTimeKeyDown}
                variant="filled"
                inputProps={{ step: 1 }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!isEditMode}
                error={
                  !formValidity.endTime.input ||
                  !formValidity.endTime.afterPrevious
                }
                helperText={
                  !formValidity.endTime.input
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.EndTimeError',
                        'End Time must be a valid time.'
                      )
                    : !formValidity.endTime.afterPrevious
                    ? t(
                        'Admin.Notifications.NotificationInformationCard.EndDateBeforeStartDateError',
                        'End Time must be after the Start Time.'
                      )
                    : ''
                }
              />
            </Box>
          </Box>

          <Box>
            <TextField
              multiline
              name="body"
              value={notificationDetails?.body ?? ''}
              onChange={handleChange}
              rows={4}
              variant={'filled'}
              label={t(
                'Admin.Notifications.NotificationInformationCard.Message',
                'Message*'
              )}
              fullWidth
              disabled={!isEditMode}
              error={!formValidity.body.input}
              helperText={
                !formValidity.name.input
                  ? t(
                      'Admin.Notifications.NotificationInformationCard.MessageError',
                      'Message cannot be empty.'
                    )
                  : t(
                      'Admin.Notifications.NotificationInformationCard.MessagePreview',
                      'Message will show in the banner preview.'
                    )
              }
            />
          </Box>
        </form>
      </CardContent>
    </Card>
  )
}
